import React, { useRef, useState } from "react";
import PoleFormularza, { skupPierwszyBlad } from "../common/PoleFormularza";
import { apiService, setSignalRToken } from "../../api/apiService";
import styles from "./ZmianaHaslaOkno.module.css";

/**
 * [KA-14 → HASLA-LOKALNE, decyzja właściciela 2026-09-27] FORMULARZ ZMIANY WŁASNEGO HASŁA — jeden dla okna z menu
 * użytkownika, „Mojego profilu” i ekranu wymuszonej zmiany (hasło tymczasowe / po 45 dniach).
 *
 * Właściciel: „reset hasla kont lokalnych - zrob z opcja zmiany dla pracownika - przypomnienie dla tych co sa lokalnie, ze
 * haslo maja zmienic co 45 dni oraz co wazne, pozwalaj im na zmiane i dla admina ale tylko dla lokalsow”.
 *
 * Wymagania hasła stoją POD polem i pochodzą z serwera (polityka Identity z konfiguracji — `GET /users/me/password`,
 * pole `wymagania`), więc ekran mówi dokładnie to, co serwer sprawdzi. Błędy stoją przy polach (PoleFormularza).
 */

/** Polityka produkcji (ServiceCollectionExtensions.AddIdentity) — tylko gdy serwer nie podał wymagań. */
export const WYMAGANIA_DOMYSLNE = Object.freeze({ minDlugosc: 8, cyfra: true, wielkaLitera: true, malaLitera: true, znakSpecjalny: false });

/** Zdanie z wymaganiami hasła do podpowiedzi pod polem. */
export const opisWymagan = (w) => {
    const r = { ...WYMAGANIA_DOMYSLNE, ...(w || {}) };
    const czesci = [];
    if (r.wielkaLitera && r.malaLitera) czesci.push("wielka i mała litera");
    else if (r.wielkaLitera) czesci.push("wielka litera");
    else if (r.malaLitera) czesci.push("mała litera");
    if (r.cyfra) czesci.push("cyfra");
    if (r.znakSpecjalny) czesci.push("znak specjalny (np. ! # -)");
    const lista = czesci.length > 1 ? `${czesci.slice(0, -1).join(", ")} oraz ${czesci[czesci.length - 1]}` : czesci[0];
    return `Co najmniej ${r.minDlugosc} znaków${lista ? `, ${lista}` : ""}. Nowe hasło musi różnić się od obecnego.`;
};

/** Pierwsze niespełnione wymaganie (zdanie) albo null — ta sama polityka co na serwerze. */
export const bladWymagan = (haslo, w) => {
    const r = { ...WYMAGANIA_DOMYSLNE, ...(w || {}) };
    if (!haslo || haslo.length < r.minDlugosc) return `Nowe hasło musi mieć co najmniej ${r.minDlugosc} znaków.`;
    if (r.wielkaLitera && !/[A-ZĄĆĘŁŃÓŚŹŻ]/.test(haslo)) return "Dodaj wielką literę.";
    if (r.malaLitera && !/[a-ząćęłńóśźż]/.test(haslo)) return "Dodaj małą literę.";
    if (r.cyfra && !/[0-9]/.test(haslo)) return "Dodaj cyfrę.";
    if (r.znakSpecjalny && !/[^A-Za-z0-9]/.test(haslo)) return "Dodaj znak specjalny.";
    return null;
};

const FormularzZmianyHasla = ({ wymagania, onZmieniono, onAnuluj, etykietaAnuluj = "Anuluj", etykietaObecnego = "Obecne hasło" }) => {
    const [obecne, setObecne] = useState("");
    const [nowe, setNowe] = useState("");
    const [powtorzone, setPowtorzone] = useState("");
    const [bledy, setBledy] = useState({});
    const [wysylanie, setWysylanie] = useState(false);
    const formularz = useRef(null);

    const pokazBledy = (b) => {
        setBledy(b);
        setTimeout(() => skupPierwszyBlad(formularz.current), 0);
    };

    const wyczysc = () => { setObecne(""); setNowe(""); setPowtorzone(""); setBledy({}); };

    const anuluj = () => { wyczysc(); if (typeof onAnuluj === "function") onAnuluj(); };

    const zapisz = async (e) => {
        e.preventDefault();
        // [HASLA-LOKALNE poprawka 2026-09-28] Zdarzenie submit nie idzie do formularza-przodka (Modal = portal, React bąbelkuje).
        e.stopPropagation();
        const nowyBlad = {};
        if (!obecne) nowyBlad.obecne = "Podaj obecne hasło.";
        if (!nowe) nowyBlad.nowe = "Podaj nowe hasło.";
        else {
            const wym = bladWymagan(nowe, wymagania);
            if (wym) nowyBlad.nowe = wym;
            else if (obecne && nowe === obecne) nowyBlad.nowe = "Nowe hasło musi różnić się od obecnego.";
        }
        if (nowe && powtorzone !== nowe) nowyBlad.powtorzone = "Hasła różnią się.";
        if (Object.keys(nowyBlad).length > 0) { pokazBledy(nowyBlad); return; }

        setWysylanie(true);
        try {
            const res = await apiService.zmienMojeHaslo(obecne, nowe);
            if (res?.data?.accessToken) setSignalRToken(res.data.accessToken);
            wyczysc();
            if (typeof onZmieniono === "function") await onZmieniono(res?.data?.message || "Hasło zmienione.");
        } catch (err) {
            const status = err?.response?.status;
            const komunikat = err?.response?.data?.message;
            const lista = err?.response?.data?.errors;
            if (status === 400 && komunikat === "Obecne hasło jest nieprawidłowe.") pokazBledy({ obecne: komunikat });
            else if (status === 400 && err?.response?.data?.pole === "nowe") pokazBledy({ nowe: komunikat });
            else if (status === 400 && Array.isArray(lista)) pokazBledy({ nowe: lista.join(" ") });
            else setBledy({ ogolny: komunikat || "Nie udało się zmienić hasła. Spróbuj ponownie." });
        } finally {
            setWysylanie(false);
        }
    };

    return (
        <form ref={formularz} className={styles.formularz} onSubmit={zapisz} noValidate>
            <PoleFormularza etykieta={etykietaObecnego} blad={bledy.obecne} wymagane className={styles.pole}>
                <input type="password" autoComplete="current-password" className={styles.input}
                    value={obecne} onChange={(e) => setObecne(e.target.value)} />
            </PoleFormularza>
            <PoleFormularza etykieta="Nowe hasło" blad={bledy.nowe} wymagane className={styles.pole}
                podpowiedz={opisWymagan(wymagania)}>
                <input type="password" autoComplete="new-password" className={styles.input}
                    value={nowe} onChange={(e) => setNowe(e.target.value)} />
            </PoleFormularza>
            <PoleFormularza etykieta="Powtórz nowe hasło" blad={bledy.powtorzone} wymagane className={styles.pole}>
                <input type="password" autoComplete="new-password" className={styles.input}
                    value={powtorzone} onChange={(e) => setPowtorzone(e.target.value)} />
            </PoleFormularza>
            {bledy.ogolny && <p className={styles.bladOgolny} role="alert">{bledy.ogolny}</p>}
            <div className={styles.przyciski}>
                {typeof onAnuluj === "function" && (
                    <button type="button" className={styles.ghostBtn} onClick={anuluj} disabled={wysylanie}>{etykietaAnuluj}</button>
                )}
                <button type="submit" className={styles.primaryBtn} disabled={wysylanie}>
                    {wysylanie ? "Zmieniam…" : "Zmień hasło"}
                </button>
            </div>
        </form>
    );
};

export default FormularzZmianyHasla;
